import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ValuesetPicker from '@/components/ValuesetPicker';
import { Sparkles, Loader2, Check, Plus } from 'lucide-react';
import { toast } from 'sonner';

const DATA_TYPES = ['string', 'date', 'number', 'code', 'text', 'boolean', 'integer', 'float'];

export default function AddAttributeDialog({ open, onOpenChange, entities, schemaId, schemaName, onCreated }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dataType, setDataType] = useState('string');
  const [entityId, setEntityId] = useState('');
  const [groupId, setGroupId] = useState('');
  const [groups, setGroups] = useState([]);
  const [isRequired, setIsRequired] = useState(false);
  const [valuesetId, setValuesetId] = useState('');
  const [valuesets, setValuesets] = useState([]);
  const [suggesting, setSuggesting] = useState(false);
  const [suggested, setSuggested] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/valuesets').then(r => setValuesets(r.data)).catch(() => {});
      setName(''); setDescription(''); setDataType('string');
      setEntityId(''); setGroupId(''); setIsRequired(false);
      setValuesetId(''); setSuggested(false);
    }
  }, [open]);

  useEffect(() => {
    if (entityId) {
      api.get(`/entities/${entityId}`).then(r => setGroups(r.data.groups || [])).catch(() => setGroups([]));
    } else {
      setGroups([]);
    }
    setGroupId('');
  }, [entityId]);

  const handleAISuggest = async () => {
    if (!name.trim()) { toast.error('Enter an attribute name first'); return; }
    setSuggesting(true);
    try {
      const entityName = entities.find(e => e.entity_id === entityId)?.name || '';
      const r = await api.post('/attributes/ai-suggest', { name: name.trim(), entity_name: entityName, schema_name: schemaName || '' });
      if (r.data.data_type) setDataType(r.data.data_type);
      if (r.data.description) setDescription(r.data.description);
      if (r.data.is_typically_required) setIsRequired(r.data.is_typically_required);
      if (r.data.existing_valueset_id) setValuesetId(r.data.existing_valueset_id);
      setSuggested(true);
      toast.success('AI suggestions applied');
    } catch (err) { toast.error('AI suggestion failed'); }
    finally { setSuggesting(false); }
  };

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Name required'); return; }
    if (!entityId) { toast.error('Select an entity'); return; }
    setSaving(true);
    try {
      // Create the attribute
      const attrRes = await api.post('/attributes', {
        name: name.trim(), description: description.trim(), data_type: dataType,
        entity_id: entityId, group_id: (groupId && groupId !== '__none__') ? groupId : null, sort_order: 0,
      });
      const attrId = attrRes.data.attribute_id;

      // If schema context, add attribute to schema
      if (schemaId) {
        await api.post(`/schemas/${schemaId}/attributes`, {
          attribute_id: attrId,
          valueset_id: valuesetId || null,
          is_required: isRequired,
        });
      }

      toast.success(`Attribute "${name.trim()}" created`);
      onOpenChange(false);
      if (onCreated) onCreated(attrRes.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg" data-testid="add-attribute-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Add Attribute</DialogTitle>
          <DialogDescription className="sr-only">Create a new data element</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Name + AI button */}
          <div>
            <Label className="text-xs font-semibold text-[#4B5563]">Attribute Name</Label>
            <div className="flex gap-2 mt-1">
              <Input value={name} onChange={e => { setName(e.target.value); setSuggested(false); }} className="rounded-none flex-1" data-testid="attr-name-input" placeholder="e.g., BRCA1 Mutation Status" />
              <Button variant="outline" size="sm" onClick={handleAISuggest} disabled={suggesting || !name.trim()} className="rounded-none h-9 px-3 shrink-0" data-testid="ai-suggest-btn">
                {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                <span className="ml-1.5 text-xs">{suggesting ? 'Thinking...' : 'AI Suggest'}</span>
              </Button>
            </div>
            {suggested && <p className="text-[10px] text-emerald-600 mt-1 flex items-center gap-1"><Check className="w-3 h-3" /> AI suggestions applied — review and adjust</p>}
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs font-semibold text-[#4B5563]">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} className="rounded-none mt-1" data-testid="attr-desc-input" rows={2} placeholder="Brief description..." />
          </div>

          {/* Data Type + Entity */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Data Type</Label>
              <Select value={dataType} onValueChange={setDataType}>
                <SelectTrigger className="rounded-none mt-1" data-testid="attr-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {DATA_TYPES.map(t => <SelectItem key={t} value={t} className="text-xs font-mono">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Entity</Label>
              <Select value={entityId} onValueChange={setEntityId}>
                <SelectTrigger className="rounded-none mt-1" data-testid="attr-entity-select">
                  <SelectValue placeholder="Select entity..." />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {(entities || []).map(e => <SelectItem key={e.entity_id} value={e.entity_id} className="text-xs">{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Group */}
          {groups.length > 0 && (
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Group (optional)</Label>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger className="rounded-none mt-1" data-testid="attr-group-select">
                  <SelectValue placeholder="No group" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="__none__" className="text-xs">No group</SelectItem>
                  {groups.map(g => <SelectItem key={g.group_id} value={g.group_id} className="text-xs">{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Valueset */}
          {(dataType === 'code') && (
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Valueset</Label>
              <div className="mt-1">
                <ValuesetPicker
                  value={valuesetId || null}
                  onChange={v => setValuesetId(v || '')}
                  valuesets={valuesets}
                  triggerClassName="h-9 text-xs w-full"
                  testId="attr-valueset-select"
                />
              </div>
            </div>
          )}

          {/* Required toggle */}
          {schemaId && (
            <div className="flex items-center gap-3">
              <Switch checked={isRequired} onCheckedChange={setIsRequired} data-testid="attr-required-switch" />
              <Label className="text-sm text-[#4B5563]">Required in {schemaName || 'this schema'}</Label>
            </div>
          )}

          <Button onClick={handleSave} disabled={saving || !name.trim() || !entityId} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="save-attribute-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Creating...' : 'Create Attribute'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
